import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Code2, Brain, Layers, Megaphone } from 'lucide-react';

const ExpertiseSection = () => {
  const expertiseItems = [
    {
      icon: Code2,
      title: 'Full-Stack Development',
      description: 'Custom web applications, backend systems, and production deployment.',
      features: ['CMS Management (WordPress, Shopify)', 'Custom WordPress Plugins Development', 'React, JavaScript', 'Python, FastAPI, MongoDB', 'Database Design & APIs'],
      gradient: 'from-red-500 to-rose-500',
    },
    {
      icon: Brain,
      title: 'AI-Powered Systems',
      description: 'AI API & LLM integration, intelligent automation, and rapid prototyping.',
      features: ['OpenAI Integration', 'AI Chatbots & Systems', 'AI Automation'],
      gradient: 'from-rose-500 to-pink-500',
    },
    {
      icon: Layers,
      title: 'Digital & Ops Platforms',
      description: 'Digital exams & booking, student management systems, real-time feedback.',
      features: ['Exam Platforms', 'Booking Systems', 'Dashboard & Analytics'],
      gradient: 'from-orange-500 to-red-500',
    },
    {
      icon: Megaphone,
      title: 'Marketing & Branding',
      description: 'Strategic digital marketing, content creation, and brand development.',
      features: ['Social Media Marketing', 'Explainer Videos', 'Promotional Content Making'],
      gradient: 'from-pink-500 to-rose-500',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section id="expertise" className="py-20 lg:py-32 relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-2" style={{ bottom: '-60px', left: '-80px', width: '280px', height: '280px' }} />
      <div className="light-decor-blob-1" style={{ top: '10%', right: '-120px', width: '350px', height: '350px' }} />
      <div className="light-decor-circle-2" style={{ bottom: '15%', right: '5%', width: '90px', height: '90px' }} />
      <div className="light-decor-dot-2" style={{ bottom: '25%', left: '8%' }} />
      <div className="light-decor-line-2" style={{ bottom: '10%', right: '8%', width: '70px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">Expertise</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-4">
            What I <span className="text-gradient">Do</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Specialized in building production-grade systems that solve real problems
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {expertiseItems.map((item, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Card className="h-full bg-card/50 border-border/50 backdrop-blur-sm card-hover group relative overflow-hidden">
                {/* Gradient border on hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}>
                  <div className={`absolute inset-[-1px] bg-gradient-to-r ${item.gradient} rounded-xl opacity-20`} />
                </div>
                
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${item.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300`}>
                    <item.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">{item.title}</CardTitle>
                  <CardDescription className="text-muted-foreground">{item.description}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2">
                    {item.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-base text-muted-foreground font-medium">
                        <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient} mr-3`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export { ExpertiseSection };
